import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiError } from "../api/types";
import { useAuth } from "../store/auth";
import { useChat } from "../store/chat";
import { useReports } from "../store/reports";
import { useLocale } from "../hooks/useLocale";
import { AppShell } from "../components/AppShell";
import { Sidebar } from "../components/Sidebar";
import { MultiTableView } from "../components/MultiTableView";
import { ReportEditPanel } from "../components/ReportEditPanel";
import { ReportFilters } from "../components/ReportFilters";
import type { FilterValues } from "../api/types";
import {
  applyFilterDefaults,
  defaultDateRange,
  parseReportParams,
  serializeReportParams,
} from "../utils/reportParams";
import styles from "./ReportPage.module.css";

const PLACEHOLDER_RE = /\{date_(?:from|to)\}/;

// Edit-panel resize bounds and persistence. Min keeps the composer usable;
// max ensures the main report area always has at least ~360px of breathing room.
const EDIT_WIDTH_DEFAULT = 380;
const EDIT_WIDTH_MIN = 320;
const EDIT_WIDTH_STORAGE_KEY = "vizario.report.editPanelWidth";
const KEYBOARD_STEP = 24;

function readStoredEditWidth(): number {
  if (typeof window === "undefined") return EDIT_WIDTH_DEFAULT;
  const raw = window.localStorage.getItem(EDIT_WIDTH_STORAGE_KEY);
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) && n >= EDIT_WIDTH_MIN ? n : EDIT_WIDTH_DEFAULT;
}

function maxEditWidth(): number {
  if (typeof window === "undefined") return 720;
  // 280 = sidebar, 360 = minimum main area, 6 = handle column.
  return Math.max(EDIT_WIDTH_MIN, window.innerWidth - 280 - 360 - 6);
}

function clampEditWidth(w: number): number {
  return Math.min(maxEditWidth(), Math.max(EDIT_WIDTH_MIN, Math.round(w)));
}

export function ReportPage() {
  const params = useParams();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { t, tOr } = useLocale();
  const reportId = Number(params.reportId);

  const current = useReports((s) => s.current);
  const result = useReports((s) => s.result);
  const loadingDetail = useReports((s) => s.loadingDetail);
  const loadingRun = useReports((s) => s.loadingRun);
  const loadDetail = useReports((s) => s.loadDetail);
  const runReport = useReports((s) => s.run);
  const removeReport = useReports((s) => s.remove);
  const setVisibility = useReports((s) => s.setVisibility);
  const duplicateReport = useReports((s) => s.duplicate);

  const currentUser = useAuth((s) => s.currentUser);

  const createChat = useChat((s) => s.createSession);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editWidth, setEditWidth] = useState<number>(readStoredEditWidth);
  const [resizing, setResizing] = useState(false);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const onSidebarCreate = useCallback(async () => {
    const chat = await createChat();
    navigate(`/chat/${chat.id}`);
  }, [createChat, navigate]);

  // Load detail + auto-run the first time the page opens for a given report
  // id (and on id change — sidebar navigation between reports). The initial
  // selection comes from the URL when it pins something (refresh, shared
  // link), otherwise every date input defaults to the last 30 days. The first
  // run already uses those values, so what's shown always matches what ran.
  useEffect(() => {
    if (!Number.isFinite(reportId) || reportId <= 0) {
      navigate("/chat", { replace: true });
      return;
    }
    setError(null);
    // window.location instead of the useSearchParams value so writing the
    // selection back to the URL below doesn't retrigger this effect.
    const parsed = parseReportParams(
      new URLSearchParams(window.location.search),
    );
    const defaults = defaultDateRange();
    loadDetail(reportId)
      .then(() => {
        const rep = useReports.getState().current;
        // Reports whose SQL isn't date-parameterized get no dates at all —
        // otherwise the URL and the run request would carry junk values.
        const dated = rep?.sql ? PLACEHOLDER_RE.test(rep.sql) : false;
        const from = dated ? parsed.dateFrom || defaults.dateFrom : "";
        const to = dated ? parsed.dateTo || defaults.dateTo : "";
        const filters = applyFilterDefaults(
          parsed.filters,
          rep?.filters ?? [],
          defaults,
        );
        setDateFrom(from);
        setDateTo(to);
        setFilterValues(filters);
        return runReport(reportId, {
          dateFrom: from || undefined,
          dateTo: to || undefined,
          filters,
        });
      })
      .catch((err) => {
        if (err instanceof ApiError) {
          setError(tOr(`errors.${err.code}`, "errors.unknown"));
        } else {
          setError(t("report.run_failed"));
        }
      });
  }, [reportId, loadDetail, runReport, navigate, t, tOr]);

  // Mirror the current selection into the URL so a refresh (or a shared link)
  // restores it. `replace` keeps filter tweaks out of the history stack. The
  // id guard avoids writing the previous report's selection onto a freshly
  // opened report's URL while its detail is still loading.
  useEffect(() => {
    if (!current || current.id !== reportId) return;
    setSearchParams(
      serializeReportParams({ dateFrom, dateTo, filters: filterValues }),
      { replace: true },
    );
  }, [current, reportId, dateFrom, dateTo, filterValues, setSearchParams]);

  const hasPlaceholders = useMemo(
    () => (current?.sql ? PLACEHOLDER_RE.test(current.sql) : false),
    [current?.sql],
  );

  const filters = current?.filters ?? [];
  const hasParams = hasPlaceholders || filters.length > 0;

  const onFilterChange = useCallback((filterId: string, values: string[]) => {
    setFilterValues((prev) => ({ ...prev, [filterId]: values }));
  }, []);

  async function onApply() {
    setError(null);
    try {
      await runReport(reportId, {
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        filters: filterValues,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(tOr(`errors.${err.code}`, "errors.unknown"));
      } else {
        setError(t("report.run_failed"));
      }
    }
  }

  async function onPageChange(page: number) {
    if (!result || page === result.page || page < 1 || page > result.total_pages) {
      return;
    }
    setError(null);
    try {
      await runReport(reportId, {
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        filters: filterValues,
        page,
        pageSize: result.page_size,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(tOr(`errors.${err.code}`, "errors.unknown"));
      } else {
        setError(t("report.run_failed"));
      }
    }
  }

  async function onDelete() {
    if (!window.confirm(t("report.delete_confirm"))) return;
    try {
      await removeReport(reportId);
      navigate("/chat", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(tOr(`errors.${err.code}`, "errors.unknown"));
      }
    }
  }

  // canWrite mirrors the backend rule: the report owner or any workspace owner
  // may edit / delete / (un)publish. Others get a read-only view + "duplicate".
  const canWrite =
    current != null &&
    currentUser != null &&
    (current.owner_user_id === currentUser.id || currentUser.role === "owner");
  const isMine =
    current != null &&
    currentUser != null &&
    current.owner_user_id === currentUser.id;

  async function onToggleVisibility() {
    if (!current) return;
    const next = current.visibility === "public" ? "private" : "public";
    setError(null);
    try {
      await setVisibility(reportId, next);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(tOr(`errors.${err.code}`, "errors.unknown"));
      }
    }
  }

  async function onDuplicate() {
    if (!current) return;
    setError(null);
    try {
      const created = await duplicateReport(reportId);
      navigate(`/report/${created.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(tOr(`errors.${err.code}`, "errors.unknown"));
      }
    }
  }

  function toggleEdit() {
    if (!current) return;
    setEditOpen((v) => !v);
  }

  // Persist whatever the user dragged the panel to. Skipping the very first
  // render (where width was already loaded from storage) avoids a redundant write.
  const persistedWidthRef = useRef<number>(editWidth);
  useEffect(() => {
    if (persistedWidthRef.current === editWidth) return;
    persistedWidthRef.current = editWidth;
    try {
      window.localStorage.setItem(EDIT_WIDTH_STORAGE_KEY, String(editWidth));
    } catch {
      /* quota / disabled storage — ignore, width still works in memory */
    }
  }, [editWidth]);

  // Reclamp on viewport resize so the user can never end up with an edit panel
  // that hides the main report area completely (e.g. after rotating a tablet).
  useEffect(() => {
    function onResize() {
      setEditWidth((w) => clampEditWidth(w));
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  function onHandlePointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startWidth: editWidth };
    setResizing(true);
  }

  function onHandlePointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag) return;
    // Handle is to the LEFT of the panel, so dragging left grows the panel.
    const next = clampEditWidth(drag.startWidth + (drag.startX - e.clientX));
    if (next !== editWidth) setEditWidth(next);
  }

  function endResize(e: ReactPointerEvent<HTMLDivElement>) {
    if (!dragRef.current) return;
    dragRef.current = null;
    setResizing(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* pointer capture may already be released */
    }
  }

  function onHandleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setEditWidth((w) => clampEditWidth(w + KEYBOARD_STEP));
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setEditWidth((w) => clampEditWidth(w - KEYBOARD_STEP));
    } else if (e.key === "Home") {
      e.preventDefault();
      setEditWidth(clampEditWidth(maxEditWidth()));
    } else if (e.key === "End") {
      e.preventDefault();
      setEditWidth(EDIT_WIDTH_MIN);
    }
  }

  // Called by the edit panel after a successful Save — re-runs the report so the
  // newly-frozen SQL/view paint immediately.
  async function onEditPanelSaved() {
    setError(null);
    try {
      // Reload the report record to pick up the new sql/view defaults.
      await loadDetail(reportId);
      await runReport(reportId, {
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        filters: filterValues,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(tOr(`errors.${err.code}`, "errors.unknown"));
      }
    }
  }

  const tables = result
    ? [{ columns: result.columns, rows: result.rows }]
    : [];
  const pivot = result?.view ?? null;

  const panelStyle: CSSProperties = editOpen
    ? ({ "--edit-width": `${editWidth}px` } as CSSProperties)
    : {};

  return (
    <AppShell sidebar={<Sidebar onCreate={() => void onSidebarCreate()} />}>
      <div
        className={`${styles.panel} ${editOpen ? styles.panelEditing : ""}`}
        style={panelStyle}
      >
        <div className={styles.main}>
          <header className={styles.header}>
            <h2 className={styles.title}>
              {current?.name?.trim() || (loadingDetail ? t("report.title_loading") : "")}
            </h2>
            <div className={styles.actions}>
              {canWrite && (
                <button
                  type="button"
                  className={styles.actionBtn}
                  onClick={() => void onToggleVisibility()}
                  disabled={!current}
                >
                  {current?.visibility === "public"
                    ? t("report.unpublish")
                    : t("report.publish")}
                </button>
              )}
              {!isMine && (
                <button
                  type="button"
                  className={styles.actionBtn}
                  onClick={() => void onDuplicate()}
                  disabled={!current}
                >
                  {t("report.duplicate")}
                </button>
              )}
              {canWrite && (
                <button
                  type="button"
                  className={`${styles.actionBtn} ${editOpen ? styles.actionBtnActive : ""}`}
                  onClick={toggleEdit}
                  disabled={!current}
                >
                  {t("report.edit")}
                </button>
              )}
              {canWrite && (
                <button
                  type="button"
                  className={`${styles.actionBtn} ${styles.dangerBtn}`}
                  onClick={onDelete}
                  disabled={!current}
                >
                  {t("report.delete")}
                </button>
              )}
            </div>
          </header>

        {hasParams && (
          <section className={styles.params}>
            <span className={styles.paramsLabel}>{t("report.params_title")}</span>
            {hasPlaceholders && (
              <>
                <label className={styles.paramField}>
                  <span>{t("date.from")}</span>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </label>
                <label className={styles.paramField}>
                  <span>{t("date.to")}</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </label>
              </>
            )}
            {filters.length > 0 && (
              <ReportFilters
                reportId={reportId}
                filters={filters}
                values={filterValues}
                onChange={onFilterChange}
              />
            )}
            <button
              type="button"
              className={styles.applyBtn}
              onClick={() => void onApply()}
              disabled={loadingRun}
            >
              {t("report.run")}
            </button>
          </section>
        )}

          <div className={styles.scroll}>
            {error && <p className={styles.error}>{error}</p>}
            {loadingRun && <p className={styles.loading}>{t("common.loading")}</p>}
            {!error && tables.length > 0 && (
              <MultiTableView
                tables={tables}
                pivot={pivot}
                pagination={result ? {
                  page: result.page,
                  pageSize: result.page_size,
                  totalRows: result.total_rows,
                  totalPages: result.total_pages,
                  loading: loadingRun,
                  onPageChange: (page) => void onPageChange(page),
                } : undefined}
              />
            )}
            {!loadingRun && !error && tables.length === 0 && current && (
              <p className={styles.empty}>{t("report.empty_state")}</p>
            )}
          </div>
        </div>

        {editOpen && current && (
          <>
            <div
              className={`${styles.resizeHandle} ${resizing ? styles.resizing : ""}`}
              role="separator"
              aria-orientation="vertical"
              aria-valuemin={EDIT_WIDTH_MIN}
              aria-valuemax={maxEditWidth()}
              aria-valuenow={editWidth}
              aria-label={t("report.resize_handle_label")}
              tabIndex={0}
              onPointerDown={onHandlePointerDown}
              onPointerMove={onHandlePointerMove}
              onPointerUp={endResize}
              onPointerCancel={endResize}
              onKeyDown={onHandleKeyDown}
            />
            <ReportEditPanel
              reportId={reportId}
              onClose={() => setEditOpen(false)}
              onSaved={() => void onEditPanelSaved()}
            />
          </>
        )}
      </div>
    </AppShell>
  );
}
